/*
 * kesslers-sku-search.js  (v3)
 * ----------------------------------------------------------------------
 * Lets staff and customers find a product by the bare numeric portion of
 * a SKU (364967) instead of the full SKU (BURD364967).
 *
 * Shopify's search index only does prefix matching within a token, so
 * "364967" can never match "BURD364967" natively. This script does not
 * add a search field -- it rewrites the QUERY before it reaches Shopify,
 * so the existing SKU indexing does all the real work.
 *
 * How it works:
 *   1. The lookup map is NOT loaded on page load. It is fetched the first
 *      time someone focuses the search field, so visitors who never search
 *      pay nothing.
 *   2. Once a complete bare number is typed and the field goes idle, the
 *      field's value is swapped to the full SKU and an input event is
 *      dispatched. The theme's own predictive search then runs normally.
 *   3. Enter resolves to the single matching product and navigates
 *      straight to its page.
 *
 * If the map is stale or fails to load, an unknown number falls through to
 * normal search behaviour. It cannot break existing search.
 *
 * INSTALLATION -- both lines go in one Custom Liquid block in the footer
 * section group, in this order:
 *
 *   <script>window.KesslersSkuMapUrl = "{{ 'kesslers-sku-map.js' | asset_url }}";</script>
 *   <script src="{{ 'kesslers-sku-search.js' | asset_url }}" defer></script>
 *
 * Debug: append ?skudebug=1 to any URL. The flag persists across page
 * loads via sessionStorage, so it survives the redirect. ?skudebug=0
 * turns it off.
 * ----------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* Set to false to disable value substitution in the search field. */
  var ENABLE_INPUT_SUBSTITUTION = true;

  var BARE_NUMBER = /^\d{4,10}$/;
  var SUBSTITUTE_DELAY_MS = 250;
  var RESOLVE_TIMEOUT_MS = 1200;
  var MAP_WAIT_MS = 1500;

  var mapState = 'idle';   /* idle | loading | ready | failed */
  var waiters = [];
  var substituted = {};

  /* ---- debug ---- */
  var DEBUG = false;
  try {
    var flag = window.location.search.match(/skudebug=([01])/);
    if (flag) {
      DEBUG = flag[1] === '1';
      window.sessionStorage.setItem('kesslersSkuDebug', DEBUG ? '1' : '0');
    } else {
      DEBUG = window.sessionStorage.getItem('kesslersSkuDebug') === '1';
    }
  } catch (e) {}

  function log() {
    if (DEBUG && window.console && console.log) {
      console.log.apply(console, ['[kesslers-sku]'].concat([].slice.call(arguments)));
    }
  }

  /* ---- lazy map loading ---- */
  function flush() {
    var pending = waiters;
    waiters = [];
    for (var i = 0; i < pending.length; i++) {
      try { pending[i](); } catch (e) { log('waiter error', e); }
    }
  }

  function loadMap() {
    if (mapState === 'ready' || mapState === 'loading') return;

    var url = window.KesslersSkuMapUrl;
    if (!url) {
      mapState = 'failed';
      log('no map URL -- is the Custom Liquid block missing its first line?');
      flush();
      return;
    }

    mapState = 'loading';
    log('loading map');

    var tag = document.createElement('script');
    tag.src = url;
    tag.async = true;
    tag.onload = function () {
      mapState = window.KESSLERS_SKU_MAP ? 'ready' : 'failed';
      log('map ' + mapState,
        mapState === 'ready' ? Object.keys(window.KESSLERS_SKU_MAP).length + ' keys' : '');
      flush();
    };
    tag.onerror = function () {
      mapState = 'failed';
      log('map failed to load');
      flush();
    };
    (document.head || document.documentElement).appendChild(tag);
  }

  function whenMapReady(callback, timeoutMs) {
    if (mapState === 'ready' || mapState === 'failed') {
      callback();
      return;
    }
    loadMap();

    var done = false;
    var timer = window.setTimeout(function () {
      if (done) return;
      done = true;
      log('map wait timed out');
      callback();
    }, timeoutMs || MAP_WAIT_MS);

    waiters.push(function () {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      callback();
    });
  }

  function lookup(value) {
    var map = window.KESSLERS_SKU_MAP;
    if (!map || !value) return null;
    var term = String(value).trim();
    if (!BARE_NUMBER.test(term)) return null;
    return Object.prototype.hasOwnProperty.call(map, term) ? map[term] : null;
  }

  function isSearchField(el) {
    return !!(el && el.tagName === 'INPUT' && el.name === 'q');
  }

  /* ---- warm the map as soon as someone touches search ---- */
  document.addEventListener('focusin', function (event) {
    if (isSearchField(event.target)) loadMap();
  }, true);

  /* ---- substitute the value once typing settles ---- */
  if (ENABLE_INPUT_SUBSTITUTION) {
    var timer = null;

    document.addEventListener('input', function (event) {
      var field = event.target;
      if (!isSearchField(field)) return;

      loadMap();
      window.clearTimeout(timer);

      var typed = String(field.value || '').trim();
      if (!BARE_NUMBER.test(typed)) return;

      timer = window.setTimeout(function () {
        whenMapReady(function () {
          /* The field may have moved on while we waited. */
          if (String(field.value || '').trim() !== typed) return;
          var sku = lookup(typed);
          if (!sku) return;
          log('substituting', typed, '->', sku);
          substituted[sku] = true;
          field.value = sku;
          field.dispatchEvent(new Event('input', { bubbles: true }));
          field.dispatchEvent(new Event('keyup', { bubbles: true }));
        });
      }, SUBSTITUTE_DELAY_MS);
    }, true);
  }

  /* ---- Enter goes straight to the product ---- */
  function go(url) {
    window.location.assign(url);
  }

  function searchUrl(term) {
    return '/search?q=' + encodeURIComponent(term) + '&type=product';
  }

  function resolve(sku) {
    var fallback = searchUrl(sku);
    var settled = false;

    var timer = window.setTimeout(function () {
      if (settled) return;
      settled = true;
      log('resolve timed out, falling back');
      go(fallback);
    }, RESOLVE_TIMEOUT_MS);

    var endpoint = '/search/suggest.json?q=' + encodeURIComponent(sku) +
      '&resources[type]=product&resources[limit]=3' +
      '&resources[options][fields]=variants.sku';

    fetch(endpoint, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        var products = (data && data.resources && data.resources.results &&
          data.resources.results.products) || [];
        if (products.length === 1 && products[0].url) {
          log('resolved to', products[0].url);
          go(products[0].url);
        } else {
          log('got ' + products.length + ' results, showing search page');
          go(fallback);
        }
      })
      .catch(function (err) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        log('resolve failed', err);
        go(fallback);
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute) return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/search') === -1) return;

    var field = form.querySelector('input[name="q"]');
    if (!field) return;

    var value = String(field.value || '').trim();
    var wasSubstituted = Object.prototype.hasOwnProperty.call(substituted, value);
    if (!BARE_NUMBER.test(value) && !wasSubstituted) return;

    event.preventDefault();
    event.stopPropagation();

    whenMapReady(function () {
      var sku = wasSubstituted ? value : lookup(value);
      if (sku) {
        log('submit intercepted:', value, '->', sku);
        resolve(sku);
      } else {
        /* Not a known number -- behave exactly like a normal submit. */
        log('no match for', value);
        go(searchUrl(value));
      }
    });
  }, true);

  log('v3 ready, map loads on first search focus');
})();
